import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const { login, isAuthenticated, error, clearError, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated and auth initialization is complete
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  // Clear error when component mounts
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // clear inline error for the field as user types
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    // simple email regex
    const re = /^\S+@\S+\.\S+$/;
    if (!re.test(email)) return 'Enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Inline validation
      const emailErr = validateEmail(formData.email);
      const passErr = validatePassword(formData.password);

      if (emailErr || passErr) {
        setErrors({ email: emailErr, password: passErr });
        // focus first invalid field
        if (emailErr) {
          emailRef.current?.focus();
        } else if (passErr) {
          passwordRef.current?.focus();
        }
        setIsLoading(false);
        return;
      }

      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/app/dashboard');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('An unexpected error occurred. Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow">
        <div className="text-center mb-6">
          <Shield className="mx-auto h-10 w-10 text-primary-600" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Sign in to your account</h2>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <Input
              id="email"
              name="email"
              type="email"
              ref={emailRef}
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={(e) => setErrors(prev => ({ ...prev, email: validateEmail(e.target.value) }))}
              placeholder="your.email@company.com"
              error={errors.email}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                ref={passwordRef}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                onBlur={(e) => setErrors(prev => ({ ...prev, password: validatePassword(e.target.value) }))}
                placeholder="Enter your password"
                error={errors.password}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
              </button>
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div>
            <Button type="submit" loading={isLoading} className="w-full">Sign in</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
